import { useState, useEffect } from "react";
import { useEasterEggs } from "../hooks/useEasterEggs";
import HeroSection from "./HeroSection";
import StatsDashboard from "./StatsDashboard";
import ComicPanel from "./ComicPanel";
import MiniGame from "./MiniGame";
import AchievementsPanel from "./AchievementsPanel";
import NotificationStack from "./NotificationStack";
import ParticleEffect from "./ParticleEffect";
import ComicStrip from "./ComicStrip";
import MuscleTraining from "./MuscleTraining";
import NewsPanel from "./NewsPanel";
import GameCenter from "./GameCenter";
import SocialFeed from "./SocialFeed";

const MainPage = () => {
	const { ultraMode } = useEasterEggs();
	const [showSecretPanel, setShowSecretPanel] = useState(false);
	const [showCopied, setShowCopied] = useState(false);

	// Konami Code sequence
	const sequence = [
		"ArrowUp",
		"ArrowUp",
		"ArrowDown",
		"ArrowDown",
		"ArrowLeft",
		"ArrowRight",
		"ArrowLeft",
		"ArrowRight",
		"b",
		"a",
	];
	const [currentSequence, setCurrentSequence] = useState<string[]>([]);

	const handleKeyPress = (e: KeyboardEvent) => {
		const newSequence = [...currentSequence, e.key];
		if (newSequence.length > sequence.length) {
			newSequence.shift();
		}
		setCurrentSequence(newSequence);

		if (newSequence.join("") === sequence.join("")) {
			setShowSecretPanel(true);
			setCurrentSequence([]);
		}
	};

	useEffect(() => {
		window.addEventListener("keydown", handleKeyPress);
		return () => window.removeEventListener("keydown", handleKeyPress);
	}, [currentSequence]);

	return (
		<div
			className={`min-h-screen bg-gradient-to-b from-trump-blue via-trump-red to-trump-yellow overflow-hidden relative ${
				ultraMode ? "animate-mega-shake" : ""
			}`}>
			<ParticleEffect />

			{/* Header com animação flutuante */}
			<header className='relative z-10 w-[90%] max-w-[2560px] mx-auto pt-8'>
				<div className='text-center mb-12 animate-float'>
					<h1 className='text-6xl md:text-8xl font-black text-trump-yellow drop-shadow-glow animate-pulse shine-effect'>
						$TRUMP TOKEN
					</h1>

					{/* Novo bloco do contrato */}
					<div className='mt-4 flex flex-col items-center gap-2'>
						<div className='group relative'>
							<div
								className='bg-black/30 px-6 py-3 rounded-full flex items-center gap-3 hover:bg-white/20 transition-all duration-300 cursor-pointer'
								onClick={() => {
									navigator.clipboard.writeText(
										"0x8a66f8af2802057a09c0de7a2c4ce01498c914ee"
									);
									setShowCopied(true);
									setTimeout(() => setShowCopied(false), 2000);
								}}>
								<span className='text-base md:text-lg font-mono text-trump-yellow break-all'>
									0x8a66f8af2802057a09c0de7a2c4ce01498c914ee
								</span>
								<span className='text-trump-yellow text-xl flex-shrink-0'>
									{showCopied ? "✓" : "⎘"}
								</span>
							</div>

							{/* Tooltip */}
							<div className='absolute -top-8 left-1/2 -translate-x-1/2 bg-black/90 text-white px-3 py-1 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
								Click to copy
							</div>
						</div>
						<div className='flex items-center gap-2'>
							<span className='text-xs text-white/60'>Abstract Network</span>
							<div className='w-2 h-2 bg-green-400 rounded-full animate-pulse' />
						</div>
					</div>
					{/* Fim do novo bloco */}

					<p className='text-xl md:text-2xl text-white/80 mt-4 max-w-2xl mx-auto'>
						The most epic meme token on Abstract Network! Join the revolution! 🚀
					</p>
				</div>
			</header>

			{/* Main Content */}
			<main className='relative z-10 mx-auto w-[90%] max-w-[2560px] py-12'>
				{/* Comic Strip Section */}
				<section className='mb-16'>
					<ComicPanel delay={0.2} className='lg:col-span-3'>
						<ComicStrip />
					</ComicPanel>
				</section>

				{/* Main Grid */}
				<div className='grid gap-8 md:grid-cols-2 lg:grid-cols-3'>
					{/* Game Center */}
					<ComicPanel delay={0.4} className='lg:col-span-2'>
						<h2 className='text-3xl font-black text-trump-yellow mb-6'>
							GAME CENTER
						</h2>
						<GameCenter />
					</ComicPanel>

					{/* News Panel */}
					<ComicPanel delay={0.4}>
						<NewsPanel />
					</ComicPanel>

					{/* Stats Panel */}
					<ComicPanel delay={0.6}>
						<StatsDashboard />
					</ComicPanel>

					{/* Hero Section */}
					<ComicPanel delay={0.8} className='lg:col-span-2'>
						<HeroSection />
					</ComicPanel>
				</div>

				{/* Social Feed Section */}
				<section className='mt-16'>
					<ComicPanel delay={1}>
						<SocialFeed />
					</ComicPanel>
				</section>
			</main>

			{/* Footer */}
			<footer className='relative z-10 w-[90%] max-w-[2560px] mx-auto py-12 text-center'>
				<p className='text-black font-medium hover:text-trump-yellow transition-colors'>
					© 2024 $TRUMP Token. All rights reserved. To the moon! 🚀
				</p>
			</footer>

			{/* Overlays */}
			<AchievementsPanel ultraMode={ultraMode} />
			<NotificationStack />

			{/* Secret Panel */}
			{showSecretPanel && (
				<ComicPanel className='fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg'>
					<div className='text-center'>
						<h3 className='text-2xl font-bold text-trump-yellow mb-4'>
							SECRET UNLOCKED! 🎉
						</h3>
						<p className='text-white'>
							You've found the secret panel! Here's a special message...
						</p>
						<button
							onClick={() => setShowSecretPanel(false)}
							className='mt-4 px-4 py-2 bg-trump-red text-white rounded-lg hover:bg-trump-yellow hover:text-black transition-colors'>
							Close
						</button>
					</div>
				</ComicPanel>
			)}
		</div>
	);
};

export default MainPage;
